import { useEffect } from 'react'
import { IImage } from '@obvious.tech/constellation'
import { Entity } from '@obvious.tech/constellation/Entity'

import api, { TApiResponse, TEntity, TUseApiActionState } from 'src/utils/api'
import {
  selectLogoImageId,
  toImageSource,
  TPassportConfiguration,
} from './clientLogo'

const getPassportConfigurations = api.fetchResource<TPassportConfiguration[]>(
  Entity.getResource('PassportConfiguration'),
)

const getImageByID = api.getResourceByID<TEntity<IImage>>(
  Entity.getResource('Image'),
)

type TOptions = {
  token?: string
}

const resolveClientLogo = async ({
  token,
}: TOptions): Promise<TApiResponse<string | null>> => {
  const passportConfigurations = await getPassportConfigurations({
    token,
  })
  if (passportConfigurations._tag === 'failure') return passportConfigurations

  const imageId = selectLogoImageId(passportConfigurations.data)
  if (imageId === null)
    return {
      _tag: 'success',
      data: null,
    }

  const image = await getImageByID({
    id: imageId,
    token,
  })
  if (image._tag === 'failure') return image

  const source = toImageSource(image.data.imageData)
  if (source === null)
    return {
      _tag: 'failure',
      error: new Error(
        `Logo Image ${imageId} is not a PNG or a JPEG, the only formats a PDF report can embed.`,
      ),
    }

  return {
    _tag: 'success',
    data: source,
  }
}

export const getClientLogo = async (
  options: TOptions,
): Promise<TApiResponse<string | null>> => {
  try {
    return await resolveClientLogo(options)
  } catch (error) {
    return {
      _tag: 'failure',
      error:
        error instanceof Error
          ? error
          : new Error('Failed to resolve the client logo.', { cause: error }),
    }
  }
}

export type TUseClientLogoResult = TUseApiActionState<typeof getClientLogo>

export type TProps = {
  authorization?: string
}

const useClientLogo = ({ authorization }: TProps): TUseClientLogoResult => {
  const [clientLogo, getLogo] = api.useApiAction(getClientLogo)

  useEffect(() => {
    return getLogo({
      token: authorization,
    })
  }, [authorization, getLogo])

  return clientLogo
}

export default useClientLogo
